import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Search, ShoppingBag, KeyRound } from "lucide-react";

// Admin-Panel im Kunden-Bearbeiten-Dialog: Portal-Zugang + Artikel-Freigabeliste.
export const PortalAccessPanel = ({ customer }) => {
  const customerId = customer?.id;
  const [enabled, setEnabled] = useState(!!customer?.portal_enabled);
  const [email, setEmail] = useState(customer?.portal_email || customer?.email || "");
  const [password, setPassword] = useState("");
  const [allowed, setAllowed] = useState(new Set((customer?.allowed_item_ids || []).map(String)));
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendInvite, setSendInvite] = useState(false);

  useEffect(() => {
    api.get("/items", { params: { type: "finished", page_size: 1000 } })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : (r.data.items || [])))
      .catch(() => setItems([]));
  }, []);

  const toggle = (id) => setAllowed((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const filtered = items.filter((it) => {
    if (!q) return true;
    const t = `${it.name || ""} ${it.sku || ""}`.toLowerCase();
    return t.includes(q.toLowerCase());
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled,
        email: email.trim().toLowerCase(),
        allowed_item_ids: [...allowed],
      };
      if (password.trim()) payload.password = password.trim();
      if (sendInvite) { payload.send_invite = true; payload.portal_url = window.location.origin; }
      const { data } = await api.patch(`/customers/${customerId}/portal`, payload);
      setPassword("");
      if (sendInvite) {
        if (data.portal_invite_sent) toast.success("Einladung mit Passwort-Link gesendet.");
        else toast.warning(data.portal_invite_error || "Einladung konnte nicht gesendet werden");
      } else {
        toast.success("Portal-Zugang gespeichert");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };

  return (
    <div className="col-span-2" data-testid="portal-access-panel">
      <div className="flex items-center gap-2 mb-3">
        <ShoppingBag size={18} className="text-[var(--pop-pink)]" />
        <h3 className="font-display text-lg">Kundenportal-Zugang</h3>
      </div>

      <label className="flex items-center gap-3 mb-4 cursor-pointer select-none">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
               data-testid="portal-enabled-toggle" className="h-5 w-5 accent-[var(--pop-pink)]" />
        <span className="text-sm font-semibold">Portal-Zugang aktiv (Kunde kann sich einloggen & selbst bestellen)</span>
      </label>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <Label>Login-E-Mail</Label>
          <Input data-testid="portal-email-input" type="email" value={email}
                 onChange={(e) => setEmail(e.target.value)} placeholder="kunde@firma.de" />
        </div>
        <div>
          <Label className="flex items-center gap-1"><KeyRound size={13} /> Passwort {enabled || customer?.portal_enabled ? "(neu setzen)" : ""}</Label>
          <Input data-testid="portal-password-input" type="password" value={password}
                 onChange={(e) => setPassword(e.target.value)}
                 placeholder={enabled || customer?.portal_enabled ? "leer = unverändert" : "mind. 6 Zeichen"} />
        </div>
      </div>

      <div className="mb-2 flex items-center justify-between">
        <Label>Freigegebene Artikel ({allowed.size})</Label>
        <div className="relative w-52">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Artikel suchen…"
                 data-testid="portal-item-search" className="pl-7 h-8 text-sm" />
        </div>
      </div>
      <div className="max-h-56 overflow-y-auto rounded-xl border border-[var(--border)] divide-y divide-[var(--border)]"
           data-testid="portal-item-list">
        {filtered.length === 0 && <div className="p-3 text-sm text-[var(--text-muted)]">Keine Artikel.</div>}
        {filtered.map((it) => (
          <label key={it.id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-black/[0.03]"
                 data-testid={`portal-item-${it.sku}`}>
            <input type="checkbox" checked={allowed.has(String(it.id))} onChange={() => toggle(String(it.id))}
                   className="h-4 w-4 accent-[var(--pop-pink)]" />
            <span className="text-sm font-medium flex-1">{it.name}</span>
            <span className="text-xs font-mono text-[var(--text-muted)]">{it.sku}</span>
          </label>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-4 flex-wrap">
        <label className="flex items-center gap-2 mr-auto text-sm cursor-pointer select-none">
          <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)}
                 data-testid="portal-send-invite" className="h-4 w-4 accent-[var(--pop-pink)]" />
          Einladungs-E-Mail senden (Kunde legt Passwort selbst fest)
        </label>
        <Button type="button" onClick={save} disabled={saving} data-testid="portal-save-btn"
                className="rounded-full bg-[var(--pop-pink)] text-white hover:opacity-90 font-bold">
          {saving ? "Speichern…" : "Portal-Zugang speichern"}
        </Button>
      </div>
    </div>
  );
};

export default PortalAccessPanel;
